import { useCallback, useEffect, useMemo, useRef } from "react";
import ForceGraph2D, { ForceGraphMethods } from "react-force-graph-2d";
import { Spin } from "antd";

const COLORS = {
  highlight: "#f680c8",
  related: "hsla(237, 48%, 24%, 1)",
  genre: "#8AE79A",
  normal: "#EFEFEF",
  linkDefault: "rgba(150,150,150,0.25)",
  linkHighlight: "#FF8CF4",
  background: "hsla(235, 44%, 11%, 1)",
};

type Node = {
  id: string;
  artist_id_strm: string;
  artist_name: string;
  career_stage: number;
  genres_spotify?: string[];
  genres_strm?: string[];
  artistNodeEdges?: { artist_name: string }[];
};

type Link = { source: string | Node; target: string | Node };

type Props = {
  data?: { nodes: Node[]; links: Link[] };
  loading: boolean;
  highlightArtist?: string;
  selectedGenre?: string;
  onArtistClick?: (artistName: string) => void;
  width?: number;
  height?: number;
};

const ArtistGraphCanvas: React.FC<Props> = ({
  data,
  loading,
  highlightArtist,
  selectedGenre,
  onArtistClick,
  width,
  height,
}) => {
  const fgRef = useRef<ForceGraphMethods>();
  const savedCameraRef = useRef<{ x: number; y: number; zoom: number } | null>(null);

  const focusId = useMemo(() => {
    if (!data || !highlightArtist) return undefined;
    const target = highlightArtist.toLowerCase();
    return data.nodes.find((n) => n.artist_name?.toLowerCase() === target)?.id;
  }, [data, highlightArtist]);

  const relatedIds = useMemo(() => {
    if (!data || !focusId) return new Set<string>();
    const ids = new Set<string>();
    data.links.forEach((link) => {
      const s = typeof link.source === "string" ? link.source : link.source.id;
      const t = typeof link.target === "string" ? link.target : link.target.id;
      if (s === focusId) ids.add(t);
      if (t === focusId) ids.add(s);
    });
    return ids;
  }, [data, focusId]);

  const isHighlighted = useCallback(
    (node: Node) => focusId !== undefined && node.id === focusId,
    [focusId]
  );

  const nodeColor = useCallback(
    (node: Node) => {
      if (isHighlighted(node)) return COLORS.highlight;
      if (relatedIds.has(node.id)) return COLORS.related;
      if (selectedGenre && node.genres_spotify?.includes(selectedGenre))
        return COLORS.genre;
      return COLORS.normal;
    },
    [isHighlighted, relatedIds, selectedGenre]
  );

  const nodeOpacity = useCallback(
    (node: Node) => {
      if (!focusId) return 1;
      if (isHighlighted(node)) return 1;
      if (relatedIds.has(node.id)) return 1;
      return 0.15;
    },
    [focusId, isHighlighted, relatedIds]
  );

  const linkColor = useCallback(
    (link: Link) => {
      if (!focusId) return COLORS.linkDefault;
      const s = typeof link.source === "string" ? link.source : link.source.id;
      const t = typeof link.target === "string" ? link.target : link.target.id;
      return s === focusId || t === focusId
        ? COLORS.linkHighlight
        : "rgba(150,150,150,0.03)";
    },
    [focusId]
  );

  const nodeLabel = useCallback((node: Node) => {
    const spotifyGenres =
      node.genres_spotify?.map((g) => `<span style="display:inline-block;border:1px solid #8AE79A;padding:1px 6px;margin:2px;border-radius:3px;">${g}</span>`).join("") || "";
    const strmGenres =
      node.genres_strm?.map((g) => `<span style="display:inline-block;border:1px solid #7FA2FC;padding:1px 6px;margin:2px;border-radius:3px;">${g}</span>`).join("") || "";
    return `
      <div style="background:#1a1a1a;color:#fff;padding:8px 12px;border-radius:4px;max-width:320px;font-size:12px;">
        <div><b>${node.artist_name}</b></div>
        <div>Stage: <b>${node.career_stage}</b></div>
        ${spotifyGenres ? `<div style="margin-top:4px;">Spotify: ${spotifyGenres}</div>` : ""}
        ${strmGenres ? `<div style="margin-top:4px;">STRM: ${strmGenres}</div>` : ""}
      </div>
    `;
  }, []);

  const paintNode = useCallback(
    (rawNode: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const node = rawNode as Node & { x: number; y: number };
      const radius = Math.max(2, node.career_stage || 3);
      const opacity = nodeOpacity(node);

      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
      ctx.fillStyle = nodeColor(node);
      ctx.fill();
      ctx.strokeStyle = "#696969";
      ctx.lineWidth = 0.5;
      ctx.stroke();

      const showLabel =
        isHighlighted(node) ||
        (node.career_stage >= 8 && globalScale > 1) ||
        globalScale > 3;
      if (showLabel && node.artist_name) {
        const fontSize = Math.max(10 / globalScale, 2);
        ctx.font = `${fontSize}px Sans-Serif`;
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(node.artist_name, node.x, node.y + radius + 1);
      }
      ctx.globalAlpha = 1;
    },
    [nodeColor, nodeOpacity, isHighlighted]
  );

  useEffect(() => {
    if (highlightArtist && fgRef.current) {
      const fg = fgRef.current as any;
      savedCameraRef.current = {
        x: fg.centerAt()?.x ?? 0,
        y: fg.centerAt()?.y ?? 0,
        zoom: fg.zoom() ?? 1,
      };
    }
  }, [highlightArtist]);

  useEffect(() => {
    if (!fgRef.current || !data?.nodes.length) return;
    if (!highlightArtist && savedCameraRef.current) {
      const { x, y, zoom } = savedCameraRef.current;
      const timer = setTimeout(() => {
        (fgRef.current as any)?.centerAt(x, y, 400);
        (fgRef.current as any)?.zoom(zoom, 400);
      }, 100);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => fgRef.current?.zoomToFit(400, 40), 600);
    return () => clearTimeout(timer);
  }, [data, highlightArtist]);

  useEffect(() => {
    if (focusId || !data) return;
    let hadPins = false;
    data.nodes.forEach((n: any) => {
      if (n.fx != null || n.fy != null) {
        n.fx = undefined;
        n.fy = undefined;
        hadPins = true;
      }
    });
    if (hadPins) fgRef.current?.d3ReheatSimulation();
  }, [focusId, data]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Spin spinning />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{ position: "relative", width, height }}>
      <ForceGraph2D
        ref={fgRef}
        graphData={data}
        width={width}
        height={height}
        backgroundColor={COLORS.background}
        nodeRelSize={1}
        nodeVal={(n: any) => Math.max(2, n.career_stage || 3)}
        nodeLabel={nodeLabel as any}
        nodeCanvasObject={paintNode}
        linkColor={linkColor as any}
        linkWidth={(l: any) => (linkColor(l) === COLORS.linkHighlight ? 1.5 : 0.5)}
        cooldownTicks={80}
        d3AlphaDecay={0.03}
        d3VelocityDecay={0.3}
        onEngineStop={() => fgRef.current?.pauseAnimation()}
        enableNodeDrag
        onNodeDragEnd={(node: any) => {
          node.fx = node.x;
          node.fy = node.y;
          fgRef.current?.resumeAnimation();
          fgRef.current?.d3ReheatSimulation();
        }}
        onNodeClick={(node: any) => onArtistClick?.(node.artist_name)}
        onBackgroundClick={() => onArtistClick?.("")}
      />
    </div>
  );
};

export default ArtistGraphCanvas;
